import { sharedShowApi } from '@/api/sharedShowApi'
import { useEffect, useMemo, useState, type ReactNode } from 'react'

import { env } from '@/lib/env'
import { configureApiAuth } from '@/api/client'
import { AuthContext, type AuthContextValue } from '@/features/auth/authContext'
import { isPlatformSession } from '@/features/auth/platformSession'
import { useAuthStore } from '@/stores/authStore'

/**
 * Publishes the signed-in session to the tree, and tells the HTTP layer what to
 * do when the server says that session is over.
 *
 * The store underneath is the single source of truth — non-React callers read
 * it directly. This exists so components get the session by context rather than
 * by importing a store, and so there is one place the 401 handler is installed.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [probing, setProbing] = useState(env.sharedShow)
  const status = useAuthStore((state) => state.status)
  const session = useAuthStore((state) => state.session)

  useEffect(() => {
    // The cookie is gone or expired; the persisted session was only a hint.
    configureApiAuth({ onUnauthenticated: () => useAuthStore.getState().clearSession() })
    if (env.sharedShow)
      void sharedShowApi
        .session()
        .then((value) => useAuthStore.getState().setSession(value))
        .catch(() => useAuthStore.getState().clearSession())
        .finally(() => setProbing(false))
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({
      status: probing ? 'loading' : status,
      isAuthenticated: !probing && status === 'authenticated',
      session,
      user: session?.user ?? null,
      role: session?.role ?? null,
      location: session?.location ?? null,
      isPlatform: session ? isPlatformSession(session) : false,
      today: session?.today ?? null,
      origins: session?.origins ?? [],
      stages: session?.stages ?? [],
      locations: session?.locations ?? [],
      workspace: session?.workspace ?? null,
    }),
    [status, session, probing],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
